// src/components/core/ScreenManager.jsx
import React, { useEffect } from 'react';
import { useUI } from '../../contexts/UIContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SCREENS } from '../../utils/constants';
import { t } from '../../i18n';
import withTranslation from '../../hoc/withTranslation';

// Yeni dosya yapısına göre import'lar
import DashboardScreen from '../../screens/DashboardScreen';
import ProfileScreen from '../../screens/profile/ProfileScreen';
import ProfileEditScreen from '../../screens/profile/ProfileEditScreen.jsx';
import ContactSettingsScreen from '../../screens/profile/ContactSettingsScreen';
import SecuritySettingsScreen from '../../screens/profile/SecuritySettingsScreen';
import DigitalVaultScreen from '../../screens/DigitalVaultScreen';
import QRScannerScreen from '../../screens/QRScannerScreen';
import GameHubScreen from '../../screens/GameHubScreen';
import MessagingScreen from '../../screens/MessagingScreen';
import NotificationsScreen from '../../screens/NotificationsScreen';
import IdentityCircleScreen from '../../screens/IdentityCircleScreen';
import TwoFactorAuthScreen from '../../screens/TwoFactorAuthScreen';
import EndlessNotesScreen from '../../screens/EndlessNotesScreen';
import LanguageAndRegionScreen from '../../screens/LanguageAndRegionScreen';
import SignupScreen from '../../screens/SignupScreen';
import WelcomeScreen from '../../screens/WelcomeScreen';
import TodayScreen from '../../screens/TodayScreen';
import OnboardingScreen from '../../screens/onboarding/OnboardingScreen'; // New
import LoginScreen from '../../screens/auth/LoginScreen';

import ForgotPasswordScreen from '../../screens/auth/ForgotPasswordScreen';
import PhoneInputScreen from '../../screens/PhoneInputScreen';
import VerificationScreen from '../../screens/VerificationScreen';
import InterestSelectionScreen from '../../screens/InterestSelectionScreen';
import AccountCreatedSuccessScreen from '../../screens/AccountCreatedSuccessScreen';
import NewPasswordScreen from '../../screens/NewPasswordScreen';
import VerifyEmailCodeScreen from '../../screens/VerifyEmailCodeScreen';
import DigitalDisciplineScreen from '../../screens/DigitalDisciplineScreen';
import SettingsScreen from '../../screens/profile/SettingsScreen';
import StatisticsDashboard from '../../screens/StatisticsDashboard';
import UnifiedStatisticsScreen from '../../screens/UnifiedStatisticsScreen';

// Content Creation Screens
import TextCreationScreen from '../../screens/content/TextCreationScreen';
import VoiceCreationScreen from '../../screens/content/VoiceCreationScreen';
import ImageCreationScreen from '../../screens/content/ImageCreationScreen';
import VideoCreationScreen from '../../screens/content/VideoCreationScreen';
import AuthLayout from '../../layouts/AuthLayout';
// Navigation
import BottomNavigation from '../BottomNavigation';

/**
 * Ekran Yöneticisi Bileşeni
 * Tüm ekranların yönetimini ve çeviri desteğini sağlar
 */
const ScreenManager = ({ t, currentLanguage }) => {
  const { currentScreen, setScreenContent } = useUI();
  const { isChangingLanguage } = useLanguage();

  // İçerik kaydedici
  const contentRef = React.useRef(null);

  // İçeriğin referansını UI context'e kaydet
  React.useEffect(() => {
    if (contentRef.current) {
      if (typeof setScreenContent === 'function') {
        setScreenContent(contentRef.current);
      }
    }
  }, [currentScreen, setScreenContent]);

  // Sayfa yüklendiğinde veya dil değiştiğinde başlık güncellenmesi
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const screenTitles = {
        [SCREENS.DASHBOARD]: t('dashboardTitle', 'Ana Sayfa'),
        [SCREENS.PROFILE]: t('profileTitle', 'Profil'),
        [SCREENS.DIGITAL_VAULT]: t('digitalVaultTitle', 'Dijital Kasa'),
        [SCREENS.QR_SCANNER]: t('qrScannerTitle', 'QR Kod Tarayıcı'),
        [SCREENS.GAME_HUB]: t('gameHubTitle', 'Oyun Merkezi'),
        [SCREENS.MESSAGING]: t('messagingTitle', 'Mesajlaşma'),
        [SCREENS.NOTIFICATIONS]: t('notificationsTitle', 'Bildirimler'),
        [SCREENS.TODAY]: t('todayTitle', 'Bugün'),
        [SCREENS.TWO_FACTOR_AUTH]: t('twoFactorAuthTitle', 'İki Faktörlü Kimlik Doğrulama'),
        [SCREENS.ENDLESS_NOTES]: t('endlessNotesTitle', 'Sonsuz Notlar'),
        [SCREENS.WELCOME]: t('welcomeTitle', 'Hoş Geldiniz'),
        [SCREENS.LOGIN]: t('loginTitle', 'Giriş'),
        [SCREENS.SIGNUP]: t('signupTitle', 'Kayıt Ol'),
        [SCREENS.SIGNUP_PHONE]: t('phoneSignupTitle', 'Telefon ile Kayıt'),
        [SCREENS.VOICE_SIGNUP]: t('voiceSignupTitle', 'Sesli Kayıt'),
        [SCREENS.VERIFY_CODE]: t('verifyCodeTitle', 'Kod Doğrulama'),
        [SCREENS.ACCOUNT_INFO]: t('accountInfoTitle', 'Hesap Bilgileri'),
        [SCREENS.INTEREST_SELECTION]: t('interestSelectionTitle', 'İlgi Alanları'),
        [SCREENS.ACCOUNT_CREATED_SUCCESS]: t('accountCreatedTitle', 'Hesap Oluşturuldu'),
        [SCREENS.FORGOT_PASSWORD]: t('forgotPasswordTitle', 'Şifremi Unuttum'),
        [SCREENS.NEW_PASSWORD]: t('newPasswordTitle', 'Yeni Şifre'),
        'verifyEmailCode': t('verifyEmailCodeTitle', 'E-posta Kodu Doğrulama'),
        'verifyPhoneCode': t('verifyPhoneCodeTitle', 'Telefon Kodu Doğrulama'),
        'profileEdit': t('profileEditTitle', 'Profili Düzenle'),
        'contactSettings': t('contactSettingsTitle', 'İletişim Ayarları'),
        'securitySettings': t('securitySettingsTitle', 'Güvenlik Ayarları'),
        'identityCircle': t('identityCircleTitle', 'IdentityCircle'),
        'digitalDiscipline': t('digitalDisciplineTitle', 'Dijital Disiplin'),
        'unifiedStatistics': t('unifiedStatisticsTitle', 'İstatistikler'),
        'textCreation': t('textCreationTitle', 'Metin Oluştur'),
        'voiceCreation': t('voiceCreationTitle', 'Ses Oluştur'),
        'imageCreation': t('imageCreationTitle', 'Görsel Oluştur'),
        'videoCreation': t('videoCreationTitle', 'Video Oluştur'),
      };

      document.title = `IDapps - ${screenTitles[currentScreen] || 'Uygulama'}`;
    }
  }, [currentScreen, currentLanguage, t]);

  // Dil değişimi sırasında yükleme göster
  if (isChangingLanguage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-indigo-600 font-medium">{t('changingLanguage', 'Dil değiştiriliyor...')}</p>
        </div>
      </div>
    );
  }

  // Ekran içeriğini oluştur - AuthLayout OLMADAN
  const renderScreenContent = () => {
    switch (currentScreen) {
      case SCREENS.DASHBOARD:
        return <DashboardScreen />;
      case SCREENS.PROFILE:
        return <ProfileScreen />;
      case 'profileEdit':
        return <ProfileEditScreen />;
      case 'contactSettings':
        return <ContactSettingsScreen />;
      case 'securitySettings':
        return <SecuritySettingsScreen />;
      case SCREENS.DIGITAL_VAULT:
        return <DigitalVaultScreen />;
      case SCREENS.QR_SCANNER:
        return <QRScannerScreen />;
      case SCREENS.GAME_HUB:
        return <GameHubScreen />;
      case SCREENS.MESSAGING:
        return <MessagingScreen />;
      case SCREENS.NOTIFICATIONS:
        return <NotificationsScreen />;
      case SCREENS.TODAY:
        return <TodayScreen />;
      case SCREENS.IDENTITY_CIRCLE:
      case 'identityCircle':
        return <IdentityCircleScreen />;
      case SCREENS.TWO_FACTOR_AUTH:
        return <TwoFactorAuthScreen />;
      case SCREENS.ENDLESS_NOTES:
        return <EndlessNotesScreen />;
      case SCREENS.WELCOME:
      case 'welcome':
        return <WelcomeScreen />;
      case SCREENS.LOGIN:
      case 'login':
        return <LoginScreen />;
      case SCREENS.SIGNUP:
      case 'signup':
        return <SignupScreen />;
      case SCREENS.ONBOARDING: // New
      case 'onboarding':
        return <OnboardingScreen />;
      case SCREENS.SIGNUP_PHONE:
      case 'signupPhone':
        return <PhoneInputScreen />;
      case SCREENS.VOICE_SIGNUP:
      case 'voiceSignup':
        return <SignupScreen />;
      case SCREENS.VERIFY_CODE:
      case 'verifyCode':
        return <VerificationScreen />;
      case 'verifyEmailCode':
        return <VerifyEmailCodeScreen />;
      case 'verifyPhoneCode':
        return <VerificationScreen />;
      case SCREENS.ACCOUNT_INFO:
      case 'accountInfo':
        console.log('AccountInfoScreen kaldırıldı, WelcomeScreen gösteriliyor');
        return <WelcomeScreen />;
      case SCREENS.INTEREST_SELECTION:
      case 'interestSelection':
        return <InterestSelectionScreen />;
      case SCREENS.ACCOUNT_CREATED_SUCCESS:
      case 'accountCreatedSuccess':
        return <AccountCreatedSuccessScreen />;
      case SCREENS.FORGOT_PASSWORD:
      case 'forgotPassword':
        return <ForgotPasswordScreen />;
      case SCREENS.NEW_PASSWORD:
      case 'newPassword':
        return <NewPasswordScreen />;
      case SCREENS.LANGUAGE_AND_REGION:
      case 'languageAndRegion':
        return <LanguageAndRegionScreen />;
      case 'settings':
        return <SettingsScreen />;
      case SCREENS.STATISTICS:
      case 'istatistikler':
        return <StatisticsDashboard />;
      case SCREENS.UNIFIED_STATISTICS:
      case 'unifiedStatistics':
        return <UnifiedStatisticsScreen />;
      case 'digitalDiscipline':
        return <DigitalDisciplineScreen />;
      case SCREENS.DIGITAL_DISCIPLINE:
        return <DigitalDisciplineScreen />;

      // Content Creation Screens
      case SCREENS.TEXT_CREATION:
      case 'textCreation':
        return <TextCreationScreen />;
      case SCREENS.VOICE_CREATION:
      case 'voiceCreation':
        return <VoiceCreationScreen />;
      case SCREENS.IMAGE_CREATION:
      case 'imageCreation':
        return <ImageCreationScreen />;
      case SCREENS.VIDEO_CREATION:
      case 'videoCreation':
        return <VideoCreationScreen />;

      default:
        console.log(`Bilinmeyen ekran: ${currentScreen}, WelcomeScreen gösteriliyor`);
        return <WelcomeScreen />;
    }
  };

  // Auth ekranları için kontrol
  const isAuthScreen = [
    SCREENS.WELCOME, 'welcome',
    SCREENS.LOGIN, 'login',
    SCREENS.SIGNUP, 'signup',
    SCREENS.VOICE_SIGNUP, 'voiceSignup',
    SCREENS.FORGOT_PASSWORD, 'forgotPassword',
    SCREENS.VERIFY_CODE, 'verifyCode', // Verify Code genelde auth flow parçasıdır
    SCREENS.ONBOARDING, 'onboarding', // New: Onboarding uses AuthLayout
    'verifyEmailCode'
  ].includes(currentScreen);

  // Screens that should show BottomNavigation
  const showBottomNav = [
    SCREENS.DASHBOARD,
    SCREENS.PROFILE,
    SCREENS.DIGITAL_VAULT,
    SCREENS.QR_SCANNER,
    // SCREENS.GAME_HUB, // GameHub might be full screen?
    // SCREENS.MESSAGING, // MessagingScreen handles its own navigation visibility
    // SCREENS.NOTIFICATIONS,
    SCREENS.TODAY,
    SCREENS.IDENTITY_CIRCLE, 'identityCircle',
    SCREENS.STATISTICS, 'istatistikler', 'unifiedStatistics', 'digitalDiscipline'
  ].includes(currentScreen);

  // Map screen to active item
  const getActiveNavItem = () => {
    switch (currentScreen) {
      case SCREENS.IDENTITY_CIRCLE:
      case 'identityCircle':
        return 'identityCircle';
      case SCREENS.MESSAGING:
        return 'messages';
      case SCREENS.QR_SCANNER:
        return 'qr';
      case SCREENS.TODAY:
        return 'today';
      case SCREENS.PROFILE:
      case 'settings':
      case 'profileEdit':
        return 'profile';
      default:
        return '';
    }
  };

  // İçerik ref ile beraber render et
  return (
    <div ref={contentRef} style={{ height: '100%', width: '100%' }}>
      {isAuthScreen ? (
        <AuthLayout currentScreen={currentScreen}>
          {renderScreenContent()}
        </AuthLayout>
      ) : (
        <>
          {renderScreenContent()}
          {showBottomNav && (
            <BottomNavigation
              activeItem={getActiveNavItem()}
              t={t}
            />
          )}
        </>
      )}
    </div>
  );
};

// withTranslation HOC ile sar
export default withTranslation(ScreenManager);